// frontend/modules/notification-config/components/NotificationConfigFormModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Alert,
  Typography,
  Checkbox,
  Space,
  Spin,
  message,
  Divider
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  NotificationPlatforms,
  Languages,
  NotificationConfigurationDTO
} from '@/shared/api/model';
import {
  useTestScriptExecution,
  useGetAllScripts,
  getScriptById
} from '@/shared/api/client/context-script-controller';
import { useTemplates } from '@/modules/template/hooks/useTemplates';
import { useNotificationConfigs } from '../hooks/useNotificationConfigs';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

// Simplified interface for contact group selection
interface ContactGroupSelection {
  [groupKey: string]: boolean; // Just track which groups are selected
}

// Available contact group from script
interface AvailableContactGroup {
  label: string;
  description?: string;
}

// Hook for testing scripts and extracting available contact groups
const useScriptTester = () => {
  const [availableContactGroups, setAvailableContactGroups] = useState<Record<string, AvailableContactGroup>>({});
  const [isTestingScript, setIsTestingScript] = useState(false);

  const testScriptMutation = useTestScriptExecution({
    mutation: {
      onError: (error: any) => {
        console.error('Error testing script:', error);
        setAvailableContactGroups({});
        setIsTestingScript(false);
      }
    }
  });

  const testScriptForContactGroups = async (scriptId: string) => {
    if (!scriptId) {
      setAvailableContactGroups({});
      return;
    }

    setIsTestingScript(true);
    try {
      const response = await getScriptById(scriptId);

      if (response) {
        const script = response;
        const sampleInput = script.sampleInput || '{}';

        const testResponse = await testScriptMutation.mutateAsync({
          data: {
            scriptContent: script.scriptContent,
            sampleContextJson: sampleInput,
            testRecipientResolution: true
          }
        });

        if (testResponse?.overallSuccess) {
          const transformedContext = testResponse.executionResult?.transformedContext;

          if (transformedContext && transformedContext.availableContactGroups) {
            // Type assertion since we know the structure from the backend
            const groups = transformedContext.availableContactGroups as Record<string, AvailableContactGroup>;
            setAvailableContactGroups(groups);
            console.log('Extracted contact groups:', groups);
          } else {
            console.log('No contact groups found in transformed context');
            setAvailableContactGroups({});
          }
        } else {
          console.error('Script test failed:', testResponse?.message);
          setAvailableContactGroups({});
        }
      }
    } catch (error) {
      console.error('Error testing script:', error);
      setAvailableContactGroups({});
    } finally {
      setIsTestingScript(false);
    }
  };

  return { availableContactGroups, isTestingScript, testScriptForContactGroups };
};

// Contact Groups Selection Component
const ContactGroupsSection: React.FC<{
  availableGroups: Record<string, AvailableContactGroup>;
  selectedGroups: ContactGroupSelection;
  onChange: (selection: ContactGroupSelection) => void;
  isLoading: boolean;
}> = ({ availableGroups, selectedGroups, onChange, isLoading }) => {

  if (isLoading) {
    return (
        <div className="contact-groups-section">
          <Title level={5}>Contact Groups</Title>
          <Spin tip="Loading available contact groups from script..." />
        </div>
    );
  }

  const groupKeys = Object.keys(availableGroups);

  if (groupKeys.length === 0) {
    return (
        <div className="contact-groups-section">
          <Title level={5}>Contact Groups</Title>
          <Alert
              type="info"
              message="No contact groups available"
              description="Select a context script that exports contact groups in its transformed context."
              icon={<InfoCircleOutlined />}
          />
        </div>
    );
  }

  const handleGroupToggle = (groupKey: string, checked: boolean) => {
    const newSelection = {
      ...selectedGroups,
      [groupKey]: checked
    };
    onChange(newSelection);
  };

  const handleSelectAll = () => {
    const allSelected = groupKeys.every(key => selectedGroups[key]);
    const newSelection: ContactGroupSelection = {};

    groupKeys.forEach(key => {
      newSelection[key] = !allSelected;
    });

    onChange(newSelection);
  };

  const selectedCount = groupKeys.filter(key => selectedGroups[key]).length;
  const allSelected = selectedCount === groupKeys.length;
  const someSelected = selectedCount > 0 && selectedCount < groupKeys.length;

  return (
      <div className="contact-groups-section">
        <Title level={5}>Contact Groups</Title>
        <Text type="secondary" className="block mb-4">
          Select which contact groups from the script should receive notifications.
          The actual recipients will be resolved dynamically when sending.
        </Text>

        <div className="mb-4">
          <Checkbox
              indeterminate={someSelected}
              checked={allSelected}
              onChange={handleSelectAll}
          >
            <Text strong>
              Select All ({selectedCount}/{groupKeys.length} selected)
            </Text>
          </Checkbox>
        </div>

        <div className="space-y-3">
          {groupKeys.map(groupKey => {
            const group = availableGroups[groupKey];
            return (
                <div key={groupKey} className="border rounded-lg p-3 bg-gray-50">
                  <Checkbox
                      checked={selectedGroups[groupKey] || false}
                      onChange={(e) => handleGroupToggle(groupKey, e.target.checked)}
                      className="mb-2"
                  >
                    <Text strong>{group.label || groupKey}</Text>
                  </Checkbox>
                  {group.description && (
                      <Text type="secondary" className="block text-sm ml-6">
                        {group.description}
                      </Text>
                  )}
                </div>
            );
          })}
        </div>
      </div>
  );
};

export const NotificationConfigFormModal: React.FC = () => {
  const [form] = Form.useForm();
  const {
    selectedConfiguration,
    isEditModalVisible,
    setEditModalVisible,
    createConfig,
    updateConfig
  } = useNotificationConfigs();

  // Get context scripts and templates for dropdowns
  const { data: scriptsResponse } = useGetAllScripts();
  const scripts = useMemo(() => scriptsResponse || [], [scriptsResponse]);

  const { templates } = useTemplates();

  // State for contact group selection
  const [selectedContactGroups, setSelectedContactGroups] = useState<ContactGroupSelection>({});

  // Use the script tester hook
  const { availableContactGroups, isTestingScript, testScriptForContactGroups } = useScriptTester();

  const isEditMode = !!selectedConfiguration?.id;
  const title = isEditMode ? 'Edit Notification Configuration' : 'Add Notification Configuration';

  // Set initial form values when editing
  useEffect(() => {
    if (selectedConfiguration) {
      form.setFieldsValue({
        ...selectedConfiguration,
        active: selectedConfiguration.active ?? true
      });

      // Test the script to get available contact groups
      if (selectedConfiguration.contextScriptId) {
        testScriptForContactGroups(selectedConfiguration.contextScriptId);
      }

      // Load selected contact groups if available
      if (selectedConfiguration.contactGroups) {
        try {
          const existingSelection = JSON.parse(selectedConfiguration.contactGroups);
          setSelectedContactGroups(existingSelection);
        } catch (err) {
          console.error("Failed to parse contact groups:", err);
          setSelectedContactGroups({});
        }
      } else {
        setSelectedContactGroups({});
      }
    } else {
      form.resetFields();
      form.setFieldsValue({
        active: true,
        language: Languages.EN,
        platform: NotificationPlatforms.SMS
      });
      setSelectedContactGroups({});
    }
  }, [selectedConfiguration?.id]); // Only depend on ID to prevent infinite loops

  // Handle script change
  const handleScriptChange = (scriptId: string) => {
    if (scriptId) {
      testScriptForContactGroups(scriptId);
      // Reset contact group selection when script changes
      setSelectedContactGroups({});
    } else {
      setSelectedContactGroups({});
    }
  };

  // Platform change handler
  const handlePlatformChange = (platform: NotificationPlatforms) => {
    // Platform changed - no additional cleanup needed since we removed subject field
  };

  // Form submit handler
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Serialize selected contact groups
      const formData: Partial<NotificationConfigurationDTO> = {
        ...values,
        contactGroups: JSON.stringify(selectedContactGroups)
      };

      console.log('Submitting notification config with contact groups:', formData);

      if (isEditMode && selectedConfiguration?.id) {
        // For updates, use the wrapped updateConfig function from hook
        const configWithId = {
          ...formData,
          id: selectedConfiguration.id
        } as NotificationConfigurationDTO;

        await updateConfig(configWithId);
      } else {
        // For creates, use the wrapped createConfig function from hook
        await createConfig(formData as NotificationConfigurationDTO);
      }

      setEditModalVisible(false);
    } catch (error) {
      console.error('Form validation error:', error);
      message.error('Please check the form fields and try again.');
    }
  };

  // Get filtered templates based on platform
  const getTemplateOptions = (platform: NotificationPlatforms) => {
    return templates
        .filter(template => template.platform === platform)
        .map(template => (
            <Option key={template.id} value={template.name}>
              {template.name}
            </Option>
        ));
  };

  const selectedPlatform = Form.useWatch('platform', form);

  return (
      <Modal
          title={title}
          open={isEditModalVisible}
          onCancel={() => setEditModalVisible(false)}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => setEditModalVisible(false)}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={handleSubmit}>
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          ]}
      >
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
        >
          <Form.Item
              label="Code"
              name="code"
              rules={[
                { required: true, message: 'Code is required' },
                { pattern: /^[A-Z0-9_]+$/, message: 'Code must contain only uppercase letters, numbers, and underscores' }
              ]}
          >
            <Input placeholder="e.g., LOAN_APPROVAL_NOTIFICATION" />
          </Form.Item>

          <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g., Loan Approval Notification" />
          </Form.Item>

          <Form.Item
              label="Description"
              name="description"
          >
            <TextArea
                rows={3}
                placeholder="Brief description of when this notification is used"
            />
          </Form.Item>

          <Form.Item
              label="Platform"
              name="platform"
              rules={[{ required: true, message: 'Platform is required' }]}
          >
            <Select onChange={handlePlatformChange}>
              <Option value={NotificationPlatforms.EMAIL}>Email</Option>
              <Option value={NotificationPlatforms.SMS}>SMS</Option>
              <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
            </Select>
          </Form.Item>

          <Form.Item
              label="Context Script"
              name="contextScriptId"
              rules={[{ required: true, message: 'Context script is required' }]}
          >
            <Select
                placeholder="Select a context script"
                onChange={handleScriptChange}
                showSearch
                optionFilterProp="children"
            >
              {scripts.map((script) => (
                  <Option key={script.id} value={script.id}>
                    {script.name}
                  </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
              label="Template"
              name="templateName"
              rules={[{ required: true, message: 'Template is required' }]}
          >
            <Select placeholder="Select a template" showSearch optionFilterProp="children">
              {getTemplateOptions(selectedPlatform)}
            </Select>
          </Form.Item>

          <Form.Item
              label="Language"
              name="language"
              rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select>
              <Option value={Languages.EN}>English</Option>
              <Option value={Languages.SW}>Swahili</Option>
            </Select>
          </Form.Item>

          <Divider />

          <ContactGroupsSection
              availableGroups={availableContactGroups}
              selectedGroups={selectedContactGroups}
              onChange={setSelectedContactGroups}
              isLoading={isTestingScript}
          />

          <Divider />

          <Form.Item
              name="active"
              valuePropName="checked"
          >
            <Switch />
            <Text className="ml-2">Active</Text>
          </Form.Item>
        </Form>
      </Modal>
  );
};